import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, List, MapIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { useSearchVenues, useServiceCategories } from "@/api/hooks";
import { searchVenuesOptions } from "@/api/hooks/use-search-venues";
import { ConsumerHomeLink } from "@/components/layout/consumer-home-link";
import { ConsumerLayout } from "@/components/layout/consumer-layout";
import { toast } from "@/components/ui/toast";
import { SearchBar } from "@/features/search/search-bar/search-bar";
import { SearchFailure } from "@/features/search/search-failure";
import { MapPanel } from "@/features/search/search-map/map-panel";
import { buildSearchIntent } from "@/features/search/search-map/search-intent";
import { ResultsPanel } from "@/features/search/search-results/results-panel";
import type { SearchBarState } from "@/features/search/types";
import { useAfterViewTransition } from "@/hooks/use-after-view-transition";
import { useGeolocation } from "@/hooks/use-geolocation";
import { useMediaQuery } from "@/hooks/use-media-query";
import { resolveLocationLabel } from "@/lib/resolve-location-label";

const searchSchema = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  category_name: z.string().optional(),
  location: z.string().optional(),
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional(),
  bounds: z.string().optional(),
  location_mode: z.enum(["place", "viewport", "coords"]).optional(),
  sort: z.enum(["relevance", "distance", "rating"]).optional(),
  when_date: z.string().optional(),
  when_time: z.string().optional(),
  page: z.coerce.number().optional().default(1),
});

export const Route = createFileRoute("/search")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(searchVenuesOptions(deps));
  },
  component: SearchPage,
});

// fallow-ignore-next-line complexity
function searchParamsToBarState(search: z.infer<typeof searchSchema>): Partial<SearchBarState> {
  return {
    query: search.q ?? "",
    category: search.category ?? null,
    categoryName: search.category_name ?? null,
    location: search.location ?? null,
    lat: search.lat ?? null,
    lng: search.lng ?? null,
    bounds: search.bounds ?? null,
    locationMode: search.location_mode ?? null,
    whenDate: search.when_date ?? null,
    whenTime: search.when_time ?? null,
  };
}

function parseCsvValues(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function MobileMapFloatingHeader({
  searchBar,
  onToggle,
}: {
  searchBar: React.ReactNode;
  onToggle: () => void;
}) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 pt-[env(safe-area-inset-top)]">
      <div className="pointer-events-auto flex items-center gap-2 px-3 py-2.5">
        <ConsumerHomeLink
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-surface/90 text-ink shadow-soft1 backdrop-blur-sm transition-colors hover:bg-surface"
          aria-label="Back to home"
        >
          <ArrowLeft className="size-5" strokeWidth={2} />
        </ConsumerHomeLink>
        <div className="min-w-0 flex-1">{searchBar}</div>
        <button
          type="button"
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-surface/90 text-ink shadow-soft1 backdrop-blur-sm transition-colors hover:bg-surface"
          onClick={onToggle}
          aria-label="Show list"
        >
          <List className="size-5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

// fallow-ignore-next-line complexity
function SearchPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [hoveredVenueId, setHoveredVenueId] = useState<string | null>(null);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const mapReady = useAfterViewTransition();
  const [showMap, setShowMap] = useState(false);

  const { latitude: geoLat, longitude: geoLng, request: requestGeolocation } = useGeolocation();
  const geoRequested = useRef(false);
  const geoApplied = useRef(false);
  const hasLocation = search.lat != null || search.lng != null || search.bounds != null;

  useEffect(() => {
    if (!hasLocation && !geoRequested.current) {
      geoRequested.current = true;
      requestGeolocation();
    }
  }, [hasLocation, requestGeolocation]);

  useEffect(() => {
    let cancelled = false;
    if (geoLat != null && geoLng != null && !hasLocation && !geoApplied.current) {
      geoApplied.current = true;
      void (async () => {
        try {
          const location = await resolveLocationLabel(geoLat, geoLng);
          if (cancelled) return;
          void navigate({
            to: "/search",
            search: { ...search, lat: geoLat, lng: geoLng, location, location_mode: "coords" },
            replace: true,
          });
        } catch (error) {
          if (cancelled || (error instanceof DOMException && error.name === "AbortError")) return;
          toast({
            variant: "error",
            title: "Unable to look up your location. Search for an area or try Near me again.",
          });
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [geoLat, geoLng, hasLocation, search, navigate]);

  const { data, isLoading, isError, error, refetch, isFetching } = useSearchVenues(search);
  const visibleData = isError ? undefined : data;
  const retrySearch = () => {
    void refetch();
  };
  const { data: categoriesData } = useServiceCategories();
  const categories = categoriesData?.service_categories ?? [];
  const activeCategoryKeys = parseCsvValues(search.category);
  const activeCategoryNames =
    categories.length > 0
      ? categories
          .filter((category) => activeCategoryKeys.includes(category.key))
          .map((category) => category.name)
      : parseCsvValues(search.category_name);

  const searchIntent = buildSearchIntent(search);

  const handlePageChange = useCallback(
    (page: number) => {
      void navigate({ to: "/search", search: { ...search, page } });
    },
    [search, navigate],
  );

  const handleBoundsChange = useCallback(
    (bounds: string) => {
      void navigate({
        to: "/search",
        search: { ...search, bounds, location_mode: "viewport", page: 1 },
      });
    },
    [search, navigate],
  );

  const mobileMap = !isDesktop && showMap;

  if (mobileMap) {
    return (
      <div className="relative h-[var(--visual-vh,100dvh)] bg-fog" aria-busy={!mapReady}>
        {mapReady && (
          <MapPanel
            pins={visibleData?.pins ?? []}
            venues={visibleData?.results ?? []}
            hoveredVenueId={hoveredVenueId}
            onHoverMarker={setHoveredVenueId}
            searchLat={search.lat}
            searchLng={search.lng}
            searchBounds={search.bounds}
            searchLocationMode={search.location_mode}
            searchIntent={searchIntent}
            isLoading={isLoading}
            onBoundsChange={handleBoundsChange}
            searchAreaClassName="top-[68px]"
          />
        )}
        {isError && (
          <div className="absolute inset-x-3 top-[76px] z-20 bg-surface px-4 py-3 shadow-soft1">
            <SearchFailure error={error} onRetry={retrySearch} isRetrying={isFetching} />
          </div>
        )}
        <MobileMapFloatingHeader
          searchBar={<SearchBar mode="compact" initialParams={searchParamsToBarState(search)} />}
          onToggle={() => setShowMap(false)}
        />
      </div>
    );
  }

  const mobileBackButton = (
    <ConsumerHomeLink
      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full text-ink transition-colors hover:bg-fog"
      aria-label="Back to home"
    >
      <ArrowLeft className="size-5" strokeWidth={2} />
    </ConsumerHomeLink>
  );

  const mobileToggle = (
    <button
      type="button"
      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full text-ink transition-colors hover:bg-fog"
      onClick={() => setShowMap(true)}
      aria-label="Show map"
    >
      <MapIcon className="size-5" strokeWidth={2} />
    </button>
  );

  return (
    <ConsumerLayout
      headerContent={<SearchBar mode="compact" initialParams={searchParamsToBarState(search)} />}
      compactNav
      mobileSearchOnly
      mobileLeftAction={mobileBackButton}
      mobileRightAction={mobileToggle}
    >
      <div className="grid grid-cols-1 md:grid-cols-[minmax(360px,2fr)_3fr]">
        <div className="px-5 pb-10">
          <ResultsPanel
            results={visibleData?.results}
            meta={visibleData?.meta}
            isLoading={isLoading}
            isError={isError}
            error={error}
            onRetry={retrySearch}
            isRetrying={isFetching}
            hoveredVenueId={hoveredVenueId}
            onHover={setHoveredVenueId}
            activeCategoryNames={activeCategoryNames}
            query={search.q}
            onPageChange={handlePageChange}
            categories={categories}
          />
        </div>

        {isDesktop && (
          <div className="sticky top-[var(--consumer-header-height-compact)] h-[calc(var(--visual-vh,100vh)-var(--consumer-header-height-compact))] overflow-hidden border-l border-fog p-2">
            <div className="size-full overflow-hidden rounded-xl bg-fog" aria-busy={!mapReady}>
              {mapReady && (
                <MapPanel
                  pins={visibleData?.pins ?? []}
                  venues={visibleData?.results ?? []}
                  hoveredVenueId={hoveredVenueId}
                  onHoverMarker={setHoveredVenueId}
                  searchLat={search.lat}
                  searchLng={search.lng}
                  searchBounds={search.bounds}
                  searchLocationMode={search.location_mode}
                  searchIntent={searchIntent}
                  isLoading={isLoading}
                  onBoundsChange={handleBoundsChange}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
